import { Person } from '@/quote/domain/Person';
import { QuoteApi } from '@/quote/infrastructure/dataSource/QuoteApi';
import { MEMBER_TYPES } from '@hfc/common/src/types/MemberTypes';
import { GetQuoteUseCase } from '@/quote/application/GetQuoteUseCase';
import { QuoteInterface } from '@/quote/domain/QuoteInterface';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { AppTypeDentalOrVision, DentalAndVisionApplication } from '@/application/shared/domain/DentalAndVisionApplication';
import { ServicesApiResponse, Ploc } from '@hfc/ui-core';
import { Quote } from '@/quote/domain/Quote';

const quoteApi = new QuoteApi();
const getQuote = new GetQuoteUseCase(quoteApi);

type QuoteTypeDentalOrVision = AppTypeDentalOrVision;

export interface QuoteStoreInterface {
  dental: QuoteInterface;
  vision: QuoteInterface;
  loadingQuote: boolean;
}

const initialState: QuoteStoreInterface = {
  [APPLICATION_TYPES.DENTAL]: new QuoteInterface({
    type: 'vision',
    applicants: [],
    children: [],
    dependants: [],
    primary: new Person(MEMBER_TYPES.APPLICANT, 0),
    spouse: null,
  }),
  [APPLICATION_TYPES.VISION]: new QuoteInterface({
    type: 'dental',
    applicants: [],
    children: [],
    dependants: [],
    primary: new Person(MEMBER_TYPES.APPLICANT, 0),
    spouse: null,
  }),
  loadingQuote: false,
};

export class QuoteStore extends Ploc<QuoteStoreInterface> {

  public constructor () {
    super(initialState);
  }

  public async submitQuote (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    localStorage.setItem(`${applicationType}Quote`, JSON.stringify({
      zip_code: this.state[applicationType].zip_code,
      type: applicationType,
      isSenior: this.state[applicationType].isSenior,
      county: this.state[applicationType].county,
      effective_date: this.state[applicationType].effective_date,
      applicants: this.state[applicationType].applicants,
    }));
  }

  public async initQuoteFromApplication (application: DentalAndVisionApplication, applicationType: QuoteTypeDentalOrVision): Promise<void> {
    const applicants = application.applicants.map((person, i) => {
      return {
        applying: person.applying,
        date_of_birth: person.date_of_birth,
        gender: person.gender,
        type: person.type,
        original_index: i
      } as Person;
    }) || [];

    await this.updateQuote({
      applicants: applicants,
      primary: applicants.find((person: Person) => person.type === MEMBER_TYPES.APPLICANT),
      spouse: applicants.find((person: Person) => person.type === MEMBER_TYPES.SPOUSE) || null,
      children: applicants.filter((person: Person) => person.type === MEMBER_TYPES.CHILD) || [],
      dependants: applicants.filter((person: Person) => person.type === MEMBER_TYPES.DEPENDENT) || [],
      zip_code: application.applicants[0].main_address?.zip_code,
      county: application.applicants[0].main_address?.county,
      effective_date: application.effective_date,
      type: applicationType
    }, applicationType);

    await this.submitQuote(applicationType);
  }

  public async updateQuote (value: Partial<QuoteInterface>, applicationType: QuoteTypeDentalOrVision): Promise<void> {
    this.changeState({ ...this.state, [applicationType]: { ...this.state[applicationType], ...value } });
  }

  public async getQuoteInfo (applicationType: QuoteTypeDentalOrVision): Promise<ServicesApiResponse<Quote>> {
    this.changeState({ ...this.state, loadingQuote: true });
    const result = await getQuote.run(applicationType);
    this.changeState({ ...this.state, loadingQuote: false });

    if (result.success && result.data) {
      const applicants = result.data.applicants?.map((person, i) => {
        return {
          ...person,
          original_index: i
        };
      }) || [];

      if (applicants.length === 0) {
        applicants.push(new Person(MEMBER_TYPES.APPLICANT, 0));
      }

      await this.updateQuote({
        applicants: applicants,
        primary: applicants.find((person: Person) => person.type === MEMBER_TYPES.APPLICANT),
        spouse: applicants.find((person: Person) => person.type === MEMBER_TYPES.SPOUSE) || null,
        children: applicants.filter((person: Person) => person.type === MEMBER_TYPES.CHILD) || [],
        dependants: applicants.filter((person: Person) => person.type === MEMBER_TYPES.DEPENDENT) || [],
        zip_code: result.data.zip_code,
        county: result.data.county,
        effective_date: result.data.effective_date,
        type: applicationType
      }, applicationType);
    }

    return result;
  }

  public canAddSpouse (applicationType: QuoteTypeDentalOrVision): boolean {
    return this.state[applicationType].spouse === null;
  }

  public canAddChildren (applicationType: QuoteTypeDentalOrVision): boolean {
    return this.state[applicationType].children.length <= 9;
  }

  public canAddDependents (applicationType: QuoteTypeDentalOrVision): boolean {
    return this.state[applicationType].dependants.length <= 9;
  }

  public async addOnOriginalApplicant (person: Person, applicationType: QuoteTypeDentalOrVision): Promise<void> {
    await this.updateQuote({ applicants: [...this.state[applicationType].applicants, person] }, applicationType);
  }

  public async addPrimary (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    const newPrimary = new Person(MEMBER_TYPES.APPLICANT, this.state[applicationType].applicants.length);
    await this.addOnOriginalApplicant(newPrimary, applicationType);
    await this.updateQuote({ primary: newPrimary }, applicationType);
  }

  public async addSpouse (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    if (this.canAddSpouse(applicationType)) {
      const newSpouse = new Person(MEMBER_TYPES.SPOUSE, this.state[applicationType].applicants.length);
      await this.addOnOriginalApplicant(newSpouse, applicationType);
      await this.updateQuote({ spouse: newSpouse }, applicationType);
    }
  }

  public async addChild (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    if (this.canAddChildren(applicationType)) {
      const newChild = new Person(MEMBER_TYPES.CHILD, this.state[applicationType].applicants.length);
      await this.addOnOriginalApplicant(newChild, applicationType);
      await this.updateQuote({ children: [...this.state[applicationType].children, newChild] }, applicationType);
    }
  }

  public async addDependent (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    if (this.canAddDependents(applicationType)) {
      const newDependant = new Person(MEMBER_TYPES.DEPENDENT, this.state[applicationType].applicants.length);
      await this.addOnOriginalApplicant(newDependant, applicationType);
      await this.updateQuote({ dependants: [...this.state[applicationType].dependants, newDependant] }, applicationType);
    }
  }

  public async updateOriginalApplicantsArray (applicationType: QuoteTypeDentalOrVision, original_index: number, person: Person): Promise<void> {
    this.state[applicationType].applicants[original_index] = person;
    await this.updateQuote({ applicants: this.state[applicationType].applicants }, applicationType);
  }

  public async deleteFromOriginalApplicantsArray (applicationType: QuoteTypeDentalOrVision, original_index: number): Promise<void> {
    const index = this.state[applicationType].applicants.findIndex((person: Person) => person.original_index === original_index);
    this.state[applicationType].applicants.splice(index, 1);
    await this.updateQuote({ applicants: this.state[applicationType].applicants }, applicationType);
  }

  public async updatePerson (person: Person, index: number, applicationType: QuoteTypeDentalOrVision): Promise<void> {
    this.updateOriginalApplicantsArray(applicationType, person.original_index, person);
    if (person.type === MEMBER_TYPES.APPLICANT) {
      await this.updateQuote({ primary: person }, applicationType);
    } else if (person.type === MEMBER_TYPES.SPOUSE) {
      await this.updateQuote({ spouse: person }, applicationType);
    } else if (person.type === MEMBER_TYPES.CHILD) {
      this.state[applicationType].children[index] = person;
      await this.updateQuote({ children: this.state[applicationType].children }, applicationType);
    } else if (person.type === MEMBER_TYPES.DEPENDENT) {
      this.state[applicationType].dependants[index] = person;
      await this.updateQuote({ dependants: this.state[applicationType].dependants }, applicationType);
    }
  }

  public async updateQuoteInfo (quoteInfo: Partial<QuoteInterface>, applicationType: QuoteTypeDentalOrVision): Promise<void> {
    await this.updateQuote(quoteInfo, applicationType);
  }

  public async removeSpouse (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    const spouse = this.state[applicationType].spouse;
    if (spouse) {
      await this.deleteFromOriginalApplicantsArray(applicationType, spouse.original_index);
    }
    await this.updateQuote({ spouse: null }, applicationType);
  }

  public async removeChildren (index: number, applicationType: QuoteTypeDentalOrVision): Promise<void> {
    await this.deleteFromOriginalApplicantsArray(applicationType, this.state[applicationType].children[index].original_index);
    this.state[applicationType].children.splice(index, 1);
    await this.updateQuote({ children: this.state[applicationType].children }, applicationType);
  }

  public async removeDependents (index: number, applicationType: QuoteTypeDentalOrVision): Promise<void> {
    await this.deleteFromOriginalApplicantsArray(applicationType, this.state[applicationType].dependants[index].original_index);
    this.state[applicationType].dependants.splice(index, 1);
    await this.updateQuote({ dependants: this.state[applicationType].dependants }, applicationType);
  }

  public async removeNonPrimaryApplicantApplicants (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    await this.removeSpouse(applicationType);

    await Promise.all(
      this.state[applicationType].children.map(() =>
        this.removeChildren(0, applicationType)
      )
    );

    await Promise.all(
      this.state[applicationType].dependants.map(() =>
        this.removeDependents(0, applicationType)
      )
    );
  }

  public async resetLocalStorage (applicationType: QuoteTypeDentalOrVision): Promise<void> {
    this.changeState({ ...this.state, [applicationType]: initialState[applicationType] });
    localStorage.removeItem(`${applicationType}Quote`);
  }

}
